import { useEffect, useState, type ReactNode } from 'react'
import { ArrowLeft, Check, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  type AssistantModel,
  type AssistantStartInput,
  useAssistantProgress,
  useFindAssistantPages,
  useStartAssistant,
} from '@/api/aiAgents'
import { MAX_SITE_PAGES, type SitePagesResult } from '@/api/knowledgePages'
import { AGENT_MODELS, AgentModelGallery, isAgentTemplateCode, type AgentModelId } from './AgentModelGallery'
import { LawDomainsPicker, defaultLawDomains, lawDomainLabels, lawDomainsValid, type LawDomains } from './LawDomainsPicker'
import { SiteAddressField } from './SiteAddressField'
import { searchError } from './SiteImportDialog'
import { ImportProgress, SitePagesChecklist, usePageSelection } from './SitePages'

interface Props {
  isOpen: boolean
  onClose: () => void
  /** Back to the choice between inbound and outbound. */
  onBack: () => void
  companyId: number | undefined
  /** The site given at sign-up, prefilled. */
  companyWebsite: string | null | undefined
  /** The agent exists: open it, or open it on a test call. */
  onCreated: (agentId: number, test: boolean) => void
}

type Step = 'depart' | 'domaines' | 'site' | 'pages' | 'creation'
const STEPS = ['depart', 'site', 'pages', 'creation'] as const
const stepIndex = (step: Step) => (step === 'domaines' ? 0 : STEPS.indexOf(step))

// The dialog is a grid: without a bounded column, a long title widens it past the window.
const CONTENT = 'sm:max-w-2xl grid-cols-[minmax(0,1fr)]'
const PRIMARY = 'gap-2 bg-blue-600 hover:bg-blue-700 text-white'

/**
 * Creating an inbound agent in four steps: a starting point, the site, its pages,
 * then the import page by page. The agent is created at the end, server side, with
 * what the onboarding already knows (greeting with the company name, voice, language).
 */
export function AgentCreationAssistant({ isOpen, onClose, onBack, companyId, companyWebsite, onCreated }: Props) {
  const { t, i18n } = useTranslation()
  const ta = (key: string, opts?: Record<string, unknown>) => t(`manager.aiAgents.assistant.${key}`, opts ?? {})
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr'

  const findPages = useFindAssistantPages(companyId)
  const start = useStartAssistant(companyId)

  const [step, setStep] = useState<Step>('depart')
  const [model, setModel] = useState<AssistantModel>('vide')
  const [domains, setDomains] = useState<LawDomains>(defaultLawDomains)
  const [address, setAddress] = useState('')
  const [found, setFound] = useState<SitePagesResult | null>(null)
  const [error, setError] = useState('')
  const [importId, setImportId] = useState<string | null>(null)
  const [lastInput, setLastInput] = useState<AssistantStartInput | null>(null)
  const selection = usePageSelection(found, () => false)
  const progress = useAssistantProgress(companyId, importId)
  const statut = progress.data?.statut

  // Each opening starts over, unless an agent is still being created.
  useEffect(() => {
    if (!isOpen) return
    if (importId && statut !== 'termine' && statut !== 'echec') return
    setStep('depart')
    setModel('vide')
    setDomains(defaultLawDomains())
    setAddress(companyWebsite ?? '')
    setFound(null)
    setError('')
    setImportId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const pick = (id: AgentModelId) => {
    if (id === 'law_firm') {
      setModel('cabinet')
      setStep('domaines')
      return
    }
    setModel(isAgentTemplateCode(id) ? id : id === 'reception' ? 'reception' : 'vide')
    setStep('site')
  }

  const search = async () => {
    if (!address.trim()) return
    setError('')
    try {
      setFound(await findPages.mutateAsync(address.trim()))
      selection.reset()
      setStep('pages')
    } catch (e) {
      setError(searchError(e, (key) => t(`manager.aiAgents.siteImport.${key}`)))
    }
  }

  const create = async (input: AssistantStartInput) => {
    setError('')
    setLastInput(input)
    setImportId(null)
    setStep('creation')
    try {
      setImportId(await start.mutateAsync(input))
    } catch {
      setError(ta('startError'))
    }
  }

  const createWith = (withPages: boolean) => {
    const pages = withPages && found
      ? found.pages.filter((p) => selection.selected.includes(p.url)).map((p) => ({ url: p.url, titre: p.titre }))
      : []
    void create({
      modele: model,
      pages,
      // The agent keeps the words of its own language.
      ...(model === 'cabinet' ? { domaines: { fr: lawDomainLabels(domains, 'fr'), en: lawDomainLabels(domains, 'en') } } : {}),
    })
  }

  const backFromSite = () => setStep(model === 'cabinet' ? 'domaines' : 'depart')

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={CONTENT}>
        <Stepper current={statut === 'termine' ? STEPS.length : stepIndex(step)} labels={STEPS.map((s) => ta(`steps.${s}`))} />

        {step === 'depart' ? (
          <>
            <Header title={ta('departTitle')} subtitle={ta('departSubtitle')} />
            <AgentModelGallery models={AGENT_MODELS} onPick={pick} />
            <Footer>
              <Button type="button" variant="ghost" onClick={onBack} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {ta('back')}
              </Button>
            </Footer>
          </>
        ) : null}

        {step === 'domaines' ? (
          <>
            <Header title={t('manager.aiAgents.models.lawFirm.title')} subtitle={t('manager.aiAgents.models.lawFirm.line')} />
            <LawDomainsPicker value={domains} onChange={setDomains} lang={lang} idPrefix="assistant-law" />
            <Footer>
              <Button type="button" variant="ghost" onClick={() => setStep('depart')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {ta('back')}
              </Button>
              <Button type="button" onClick={() => setStep('site')} disabled={!lawDomainsValid(domains, lang)} className={PRIMARY}>
                {ta('continue')}
              </Button>
            </Footer>
          </>
        ) : null}

        {step === 'site' ? (
          <>
            <Header title={ta('siteTitle')} subtitle={ta('siteSubtitle', { max: MAX_SITE_PAGES })} />
            <SiteAddressField
              id="assistant-site"
              value={address}
              onChange={setAddress}
              onSubmit={search}
              searching={findPages.isPending}
              error={error}
            />
            <Footer>
              <Button type="button" variant="ghost" onClick={backFromSite} className="gap-2" disabled={findPages.isPending}>
                <ArrowLeft className="h-4 w-4" />
                {ta('back')}
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => createWith(false)} disabled={findPages.isPending}>
                  {ta('skip')}
                </Button>
                <Button type="button" onClick={search} disabled={findPages.isPending || !address.trim()} className={PRIMARY}>
                  {findPages.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('manager.aiAgents.siteImport.find')}
                </Button>
              </div>
            </Footer>
          </>
        ) : null}

        {step === 'pages' && found ? (
          <>
            <Header
              title={t('manager.aiAgents.siteImport.chooseTitle')}
              subtitle={t('manager.aiAgents.siteImport.chooseSubtitle', { max: MAX_SITE_PAGES })}
            />
            <SitePagesChecklist pages={found.pages} selected={selection.selected} onToggle={selection.toggle} />
            <Footer>
              <Button type="button" variant="ghost" onClick={() => setStep('site')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {ta('back')}
              </Button>
              <Button type="button" onClick={() => createWith(true)} disabled={!selection.selected.length} className={PRIMARY}>
                {ta('create')}
              </Button>
            </Footer>
          </>
        ) : null}

        {step === 'creation' ? (
          <Creation
            // Until the first answer of the server, the chosen pages wait.
            pages={
              progress.data?.pages ??
              (lastInput?.pages ?? []).map((p) => ({ ...p, etat: 'attente' as const, motif: '', page_id: null, caracteres: 0 }))
            }
            statut={error || progress.isError ? 'echec' : statut}
            error={error}
            ta={ta}
            onRetry={() => lastInput && void create(lastInput)}
            onBackground={onClose}
            onOpen={(test) => progress.data?.agent_id && onCreated(progress.data.agent_id, test)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function Creation({
  pages,
  statut,
  error,
  ta,
  onRetry,
  onBackground,
  onOpen,
}: {
  pages: import('@/api/knowledgePages').SiteImportPage[]
  statut: import('@/api/knowledgePages').SiteImport['statut'] | undefined
  error: string
  ta: (key: string, opts?: Record<string, unknown>) => string
  onRetry: () => void
  onBackground: () => void
  onOpen: (test: boolean) => void
}) {
  const ready = statut === 'termine'
  const failed = statut === 'echec'
  const saved = pages.filter((p) => p.etat === 'enregistree').length
  return (
    <>
      <Header title={ready ? ta('readyTitle') : failed ? ta('failedTitle') : ta('creatingTitle')} />
      {pages.length ? <ImportProgress pages={pages} /> : null}
      {ready ? (
        <p className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
          {ta('ready', { count: saved })}
        </p>
      ) : failed ? (
        <p className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
          <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error || ta('failed')}
        </p>
      ) : statut === 'creation' || (!pages.length && !error) ? (
        <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          {ta('creatingAgent')}
        </p>
      ) : null}
      <Footer>
        <span />
        {ready ? (
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpen(true)}>
              {ta('test')}
            </Button>
            <Button type="button" onClick={() => onOpen(false)} className={PRIMARY}>
              {ta('open')}
            </Button>
          </div>
        ) : failed ? (
          <Button type="button" onClick={onRetry} className={PRIMARY}>
            {ta('retry')}
          </Button>
        ) : (
          <Button type="button" variant="outline" onClick={onBackground}>
            {ta('background')}
          </Button>
        )}
      </Footer>
    </>
  )
}

function Header({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <DialogTitle className="font-display text-xl tracking-tight">{title}</DialogTitle>
      <DialogDescription className={subtitle ? undefined : 'sr-only'}>{subtitle ?? title}</DialogDescription>
    </div>
  )
}

function Footer({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 pt-1">{children}</div>
}

/** The four steps, the current one in blue, the done ones checked. */
function Stepper({ current, labels }: { current: number; labels: string[] }) {
  return (
    <ol className="flex min-w-0 items-center gap-2 pr-8 text-xs">
      {labels.map((label, i) => (
        <li key={label} className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
              i < current && 'bg-blue-600 text-white',
              i === current && 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-500/20',
              i > current && 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
            )}
          >
            {i < current ? <Check className="h-3 w-3" /> : i + 1}
          </span>
          <span
            className={cn(
              'truncate',
              i === current ? 'font-semibold text-slate-900 dark:text-white' : 'hidden sm:inline text-slate-500 dark:text-slate-400',
            )}
          >
            {label}
          </span>
          {i < labels.length - 1 ? <span className="hidden sm:block h-px w-6 bg-slate-200 dark:bg-slate-700" /> : null}
        </li>
      ))}
    </ol>
  )
}
